import { useRef, type TouchEvent } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useDialogA11y } from '../../hooks/useDialogA11y';

export interface LightboxImage {
  src: string;
  alt: string;
}

const ROUND_BUTTON =
  'tw-inline-flex tw-items-center tw-justify-center tw-w-11 tw-h-11 tw-rounded-full tw-border-0 tw-bg-white/10 tw-text-white hover:tw-bg-white/25 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-white tw-transition-colors';

/** Distance horizontale (px) à partir de laquelle un glissement du doigt change d'image. */
const SWIPE_THRESHOLD = 50;

/**
 * Visionneuse plein écran des images d'une nouveauté, avec navigation entre
 * elles : boutons, flèches du clavier, glissement du doigt. Échap, la croix ou un
 * clic sur le fond ferment. Le focus y est piégé et revient à l'image d'origine.
 *
 * Rendue dans `document.body` : elle échappe aux conteneurs à défilement et aux
 * empilements des différents layouts (barre latérale de la gestion SAV comprise).
 */
export function NewsLightbox({
  images,
  index,
  onIndexChange,
  onClose,
}: {
  images: LightboxImage[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number | null>(null);

  const count = images.length;
  const hasSeveral = count > 1;
  // Navigation circulaire : après la dernière image, la première.
  const goPrevious = () => onIndexChange((index - 1 + count) % count);
  const goNext = () => onIndexChange((index + 1) % count);

  useDialogA11y(containerRef, {
    onEscape: onClose,
    onArrowLeft: hasSeveral ? goPrevious : undefined,
    onArrowRight: hasSeveral ? goNext : undefined,
  });

  const image = images[index];
  if (!image) return null;

  const handleTouchEnd = (event: TouchEvent) => {
    const start = touchStartX.current;
    touchStartX.current = null;
    if (start === null || !hasSeveral) return;
    const delta = event.changedTouches[0].clientX - start;
    if (delta > SWIPE_THRESHOLD) goPrevious();
    else if (delta < -SWIPE_THRESHOLD) goNext();
  };

  return createPortal(
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-label="Visionneuse d'images"
      tabIndex={-1}
      className="tw-fixed tw-inset-0 tw-z-[9995] tw-bg-black/90 tw-flex tw-flex-col tw-outline-none"
      onTouchStart={(event) => {
        touchStartX.current = event.touches[0].clientX;
      }}
      onTouchEnd={handleTouchEnd}
    >
      <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-flex-shrink-0">
        <span className="tw-text-sm tw-text-white/80" aria-live="polite">
          {hasSeveral ? `Image ${index + 1} sur ${count}` : ''}
        </span>
        <button type="button" onClick={onClose} aria-label="Fermer la visionneuse" className={ROUND_BUTTON}>
          <X className="tw-w-5 tw-h-5" />
        </button>
      </div>

      {/* Scène : un clic à côté de l'image (sur le fond) ferme. */}
      <div
        className="tw-flex-1 tw-min-h-0 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-2 sm:tw-px-4 tw-pb-4"
        onClick={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
      >
        {hasSeveral && (
          <button type="button" onClick={goPrevious} aria-label="Image précédente" className={`${ROUND_BUTTON} tw-flex-shrink-0`}>
            <ChevronLeft className="tw-w-6 tw-h-6" />
          </button>
        )}

        <figure className="tw-m-0 tw-min-w-0 tw-max-h-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-flex-1 tw-pointer-events-none">
          <img
            key={image.src}
            src={image.src}
            alt={image.alt}
            className="tw-max-w-full tw-max-h-[calc(100vh-9rem)] tw-object-contain tw-rounded tw-pointer-events-auto"
          />
          {image.alt && (
            <figcaption className="tw-mt-2 tw-text-sm tw-text-white/80 tw-text-center tw-pointer-events-auto">
              {image.alt}
            </figcaption>
          )}
        </figure>

        {hasSeveral && (
          <button type="button" onClick={goNext} aria-label="Image suivante" className={`${ROUND_BUTTON} tw-flex-shrink-0`}>
            <ChevronRight className="tw-w-6 tw-h-6" />
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}
